
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Play, StopCircle, BarChart3, Users, Clock, Vote, ClipboardList, X, MoreVertical, Trash2, Edit2, Archive } from 'lucide-react';
import { Motion, MotionStatus, VotingType, UserRole, User } from '../types';
import ResultsChart from './ResultsChart';

const MeetingRoom: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('smsa_user');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [motions, setMotions] = useState<Motion[]>(() => {
    const saved = localStorage.getItem(`motions_${id}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [meeting, setMeeting] = useState(() => {
    const saved = localStorage.getItem('smsa_meetings');
    const all = saved ? JSON.parse(saved) : [];
    return all.find((m: any) => m.id === id);
  });

  const [activeTab, setActiveTab] = useState<'motions' | 'voters' | 'results'>('motions');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  
  // Motion creation states
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingMotion, setEditingMotion] = useState<Motion | null>(null);

  const isAdmin = user && [UserRole.SYSTEM_ADMIN, UserRole.PRESIDENT, UserRole.LOCAL_DIRECTOR, UserRole.NATIONAL_OFFICER, UserRole.LOCAL_OFFICER].includes(user.role);

  useEffect(() => {
    localStorage.setItem(`motions_${id}`, JSON.stringify(motions));
  }, [motions, id]);

  const activeMotion = motions.find(m => m.status === MotionStatus.ACTIVE);

  const createMotion = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle) return;

    if (editingMotion) {
      const updated = motions.map(m => m.id === editingMotion.id ? { ...m, title: newTitle, description: newDesc } : m);
      setMotions(updated);
    } else {
      const newMotion: Motion = {
        id: 'mo' + Date.now(),
        title: newTitle,
        description: newDesc,
        status: MotionStatus.DRAFT,
        type: VotingType.MAJORITY,
        options: [
          { id: '1', label: 'In Favor', votes: 0 },
          { id: '2', label: 'Against', votes: 0 },
          { id: '3', label: 'Abstain', votes: 0 }
        ],
        totalVotes: 0,
        weightedTotal: 0
      };
      setMotions([...motions, newMotion]);
    }
    closeModal();
  };

  const toggleMotionStatus = (motionId: string) => {
    setMotions(prev => prev.map(m => {
      if (m.id === motionId) {
        if (m.status === MotionStatus.ACTIVE) return { ...m, status: MotionStatus.CLOSED };
        if (m.status === MotionStatus.DRAFT) return { ...m, status: MotionStatus.ACTIVE };
      }
      return m;
    }));
  };

  const deleteMotion = (mid: string) => {
    if (window.confirm("Delete this motion record?")) {
      setMotions(motions.filter(m => m.id !== mid));
      setActiveMenuId(null);
    }
  };

  const openEdit = (m: Motion) => {
    setEditingMotion(m);
    setNewTitle(m.title);
    setNewDesc(m.description);
    setIsModalOpen(true);
    setActiveMenuId(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMotion(null);
    setNewTitle('');
    setNewDesc('');
  };

  const closeAssembly = () => {
    if (!isAdmin) return;
    if (window.confirm("ARE YOU SURE? This will PERMANENTLY CLOSE the assembly and ARCHIVE all results. No further votes can be cast.")) {
      const saved = localStorage.getItem('smsa_meetings');
      const all = saved ? JSON.parse(saved) : [];
      const updated = all.map((m: any) => m.id === id ? { ...m, status: 'ARCHIVED' } : m);
      localStorage.setItem('smsa_meetings', JSON.stringify(updated));
      navigate('/archive');
    }
  };

  const startAssembly = () => {
    if (!isAdmin) return;
    const saved = localStorage.getItem('smsa_meetings');
    const all = saved ? JSON.parse(saved) : [];
    const updated = all.map((m: any) => m.id === id ? { ...m, status: 'ONGOING' } : m);
    localStorage.setItem('smsa_meetings', JSON.stringify(updated));
    setMeeting({ ...meeting, status: 'ONGOING' });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-xl text-slate-800">{meeting?.name || 'Assembly Session'}</h2>
              {meeting?.status === 'ARCHIVED' && (
                <span className="bg-slate-800 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase">Archived</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="flex items-center gap-1 font-medium"><Clock size={14} /> {meeting?.status === 'ONGOING' ? 'Live Session' : meeting?.status === 'UPCOMING' ? 'Scheduled' : 'Session Ended'}</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span className="flex items-center gap-1"><Users size={14} /> SMSA Verified Members</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex bg-slate-100 p-1 rounded-lg">
            {(['motions', 'voters', 'results'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                  activeTab === tab ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {isAdmin && meeting?.status !== 'ARCHIVED' && (
            <div className="flex gap-2">
              {meeting?.status === 'UPCOMING' && (
                <button 
                  onClick={startAssembly}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-100"
                >
                  <Play size={18} fill="currentColor" /> Start Assembly
                </button>
              )}
              <button 
                onClick={closeAssembly}
                className="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-rose-100"
              >
                <Archive size={18} /> Finalize & Close
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 p-8 bg-slate-50 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          {activeTab === 'motions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800">Order of Business</h3>
                {isAdmin && meeting?.status !== 'ARCHIVED' && (
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-700 text-sm"
                  >
                    <Plus size={18} /> New Motion
                  </button>
                )}
              </div>

              {motions.length > 0 ? (
                motions.map((motion) => (
                  <div key={motion.id} className={`bg-white border-2 rounded-2xl p-6 transition-all relative ${motion.status === MotionStatus.ACTIVE ? 'border-indigo-500 shadow-lg' : 'border-slate-100 shadow-sm'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black tracking-wider uppercase ${
                        motion.status === MotionStatus.ACTIVE ? 'bg-indigo-100 text-indigo-700' : 
                        motion.status === MotionStatus.CLOSED ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {motion.status}
                      </span>
                      {isAdmin && meeting?.status !== 'ARCHIVED' && (
                        <div className="relative">
                          <button 
                            onClick={() => setActiveMenuId(activeMenuId === motion.id ? null : motion.id)}
                            className="p-1 text-slate-400 hover:text-indigo-600 rounded-lg"
                          >
                            <MoreVertical size={18} />
                          </button>
                          {activeMenuId === motion.id && (
                            <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-slate-100 z-20 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                               <button 
                                 onClick={() => { openEdit(motion); setActiveMenuId(null); }} 
                                 className="w-full px-4 py-2 text-left text-xs font-bold text-slate-600 hover:bg-indigo-50 flex items-center gap-2"
                               >
                                 <Edit2 size={14} /> Edit
                               </button>
                               <button 
                                 onClick={() => { deleteMotion(motion.id); setActiveMenuId(null); }} 
                                 className="w-full px-4 py-2 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 flex items-center gap-2"
                               >
                                 <Trash2 size={14} /> Delete
                               </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <h4 className="text-xl font-bold text-slate-900 mb-2">{motion.title}</h4>
                    <p className="text-slate-600 mb-6 font-medium">{motion.description}</p>

                    <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-slate-50">
                      <div className="flex gap-4">
                        <div className="text-center px-4 py-2 bg-slate-50 rounded-xl">
                          <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Total Votes</p>
                          <p className="text-sm font-bold text-slate-700">{motion.totalVotes}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {isAdmin && meeting?.status === 'ONGOING' && (
                          <>
                            {motion.status === MotionStatus.ACTIVE ? (
                              <button 
                                onClick={() => toggleMotionStatus(motion.id)}
                                className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-rose-100"
                              >
                                <StopCircle size={18} /> Close Vote
                              </button>
                            ) : motion.status === MotionStatus.DRAFT ? (
                              <button 
                                onClick={() => toggleMotionStatus(motion.id)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 transition-all"
                              >
                                <Play size={18} fill="currentColor" /> Open Vote
                              </button>
                            ) : null}
                          </>
                        )}
                        <button 
                          onClick={() => setActiveTab('results')}
                          className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-xl font-bold flex items-center gap-2"
                        >
                          <BarChart3 size={18} /> Results
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-3xl p-16 flex flex-col items-center justify-center text-center">
                  <div className="bg-slate-50 p-6 rounded-3xl mb-4 text-slate-200">
                    <ClipboardList size={64} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">No Motions Proposed</h3>
                  <p className="text-slate-500 max-w-sm font-medium mb-8">This assembly session has no items in the order of business yet.</p>
                  {isAdmin && meeting?.status !== 'ARCHIVED' && (
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                    >
                      Propose First Motion
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="animate-in fade-in duration-500 text-center py-20">
               {activeMotion || (motions.length > 0 && motions[0]) ? (
                 <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm max-w-2xl mx-auto">
                    <h3 className="text-2xl font-bold text-slate-900 mb-8">{(activeMotion || motions[0]).title}</h3>
                    <div className="h-[400px]">
                      <ResultsChart data={(activeMotion || motions[0]).options} />
                    </div>
                 </div>
               ) : (
                 <p className="text-slate-400 font-bold uppercase tracking-widest">No active voting results to display</p>
               )}
            </div>
          )}

          {activeTab === 'voters' && (
            <div className="text-center py-20">
               <p className="text-slate-400 font-bold uppercase tracking-widest">Delegate Presence Tracker Active</p>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 uppercase tracking-tight">{editingMotion ? 'Edit Motion' : 'Propose New Motion'}</h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={createMotion} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Motion Title</label>
                <input 
                  autoFocus
                  type="text" 
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Budget Approval 2024" 
                  className="w-full px-4 py-4 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl transition-all outline-none font-bold text-slate-800"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Description</label>
                <textarea 
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Full text of the motion..." 
                  className="w-full px-4 py-4 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl transition-all outline-none font-medium text-slate-800 h-32"
                />
              </div>
              <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                {editingMotion ? 'Update Proposal' : 'Register Motion'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingRoom;
